import axios from 'axios';
import config from '../config/config';
import securityConfig from '../config/securityConfig';
import { getAccessToken, refreshToken, isTokenExpired, getCsrfToken } from './authService';

const axiosInstance = axios.create({
    baseURL: config.apiUrl,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// List of public endpoints that don't need authentication
const publicEndpoints = [
    '/feed/filtered',
    '/feed/search',
    '/auth/login',
    '/auth/register',
    '/auth/refresh'
];

// Flag to prevent multiple refresh attempts simultaneously
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

// Function to add callbacks to the refresh subscribers
const subscribeToTokenRefresh = (callback: (token: string) => void) => {
    refreshSubscribers.push(callback);
};

// Function to notify all subscribers about the new token
const onRefreshSuccess = (token: string) => {
    refreshSubscribers.forEach(callback => callback(token));
    refreshSubscribers = [];
};

// Add request interceptor
axiosInstance.interceptors.request.use(
    async (config) => {
        const isPublicEndpoint = publicEndpoints.some(endpoint => 
            config.url?.includes(endpoint)
        );

        // Always include credentials for cookies
        config.withCredentials = true;

        // Skip authentication for public endpoints
        if (isPublicEndpoint) {
            return config;
        }

        // Get token
        const token = getAccessToken();
        
        if (token) {
            // Check if token is expired and needs refresh
            if (isTokenExpired(token)) {
                // Only attempt refresh if not already refreshing
                if (!isRefreshing) {
                    try {
                        isRefreshing = true;
                        const newToken = await refreshToken();
                        
                        if (newToken) {
                            // Notify all subscribers about the new token
                            onRefreshSuccess(newToken);
                            config.headers.Authorization = `Bearer ${newToken}`;
                        } else {
                            throw new Error('Token refresh failed');
                        }
                    } catch (error) {
                        // Let the response interceptor handle the error
                        console.error('Error refreshing token in request interceptor:', error);
                    } finally {
                        isRefreshing = false;
                    }
                } else {
                    // If already refreshing, wait for the new token
                    const newToken = await new Promise<string>((resolve, reject) => {
                        subscribeToTokenRefresh((token: string) => {
                            resolve(token);
                        });
                        
                        // Add a timeout to prevent hanging indefinitely
                        setTimeout(() => {
                            reject(new Error('Token refresh timeout'));
                        }, 10000);
                    });
                    
                    config.headers.Authorization = `Bearer ${newToken}`;
                }
            } else {
                // Token is valid, use it
                config.headers.Authorization = `Bearer ${token}`;
            }
        }
        
        // Add CSRF token for non-GET requests
        if (config.method?.toLowerCase() !== 'get') {
            const csrfToken = getCsrfToken();
            if (csrfToken) {
                config.headers[securityConfig.csrfHeaderName] = csrfToken;
            }
        }
        
        return config;
    },
    (error) => Promise.reject(error)
);

// Add response interceptor
axiosInstance.interceptors.response.use(
    (response) => {
        // Check for CSRF token in response headers
        const csrfToken = response.headers[securityConfig.csrfHeaderName.toLowerCase()];
        if (csrfToken) {
            // Update CSRF token in authService
            import('./authService').then(authService => {
                authService.setCsrfToken(csrfToken);
            });
        }
        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        
        // Prevent infinite loops - if this is already a retry, don't try again
        if (originalRequest._retry) {
            return Promise.reject(error);
        }
        
        // Handle 401 Unauthorized errors
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            
            // If we're not already refreshing, try to refresh the token
            if (!isRefreshing) {
                try {
                    isRefreshing = true;
                    const newToken = await refreshToken();
                    
                    if (newToken) {
                        // Update the original request with the new token
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        
                        // Notify all subscribers about the new token
                        onRefreshSuccess(newToken);
                        
                        return axios(originalRequest);
                    } else {
                        // If refresh failed, redirect to login
                        window.dispatchEvent(new Event('authExpired'));
                        return Promise.reject(error);
                    }
                } catch (refreshError) {
                    console.error('Failed to refresh token:', refreshError);
                    
                    // If refresh failed, redirect to login
                    window.dispatchEvent(new Event('authExpired'));
                    return Promise.reject(error);
                } finally {
                    isRefreshing = false;
                }
            } else {
                // If already refreshing, wait for the new token
                try {
                    const newToken = await new Promise<string>((resolve, reject) => {
                        subscribeToTokenRefresh((token: string) => {
                            resolve(token);
                        });
                        
                        // Add a timeout to prevent hanging indefinitely
                        setTimeout(() => {
                            reject(new Error('Token refresh timeout'));
                        }, 10000);
                    });
                    
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return axios(originalRequest);
                } catch (waitError) {
                    console.error('Error waiting for token refresh:', waitError);
                    return Promise.reject(error);
                }
            }
        }
        
        return Promise.reject(error);
    }
);

export default axiosInstance;
